<script setup>
import { ref } from 'vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  templates: {
    type: Array,
    default: () => [],
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['add', 'edit', 'delete', 'importCsv', 'exportCsv']);

const handleAdd = () => {
  emit('add');
};

const handleEdit = item => {
  emit('edit', item);
};

const handleDelete = item => {
  emit('delete', item);
};

const handleImportCsv = () => {
  emit('importCsv');
};

const handleExportCsv = () => {
  emit('exportCsv');
};
</script>

<template>
  <div class="flex flex-col gap-6">
    <!-- Action Buttons -->
    <div class="flex justify-end gap-3">
      <Button
        color="slate"
        variant="outline"
        icon="i-lucide-download"
        label="Import CSV"
        @click="handleImportCsv"
      />
      <Button
        color="slate"
        variant="outline"
        icon="i-lucide-upload"
        label="Export CSV"
        @click="handleExportCsv"
      />
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="flex items-center justify-center py-12">
      <span class="i-lucide-loader-2 w-8 h-8 text-woot-500 animate-spin" />
    </div>

    <!-- Empty State -->
    <div
      v-else-if="templates.length === 0"
      class="flex flex-col items-center justify-center py-16 text-center"
    >
      <span class="i-lucide-file-text w-16 h-16 text-n-slate-6 mb-4" />
      <h3 class="text-lg font-medium text-n-slate-12 mb-2">
        No answer templates yet
      </h3>
      <p class="text-sm text-n-slate-10 mb-6 max-w-md">
        Add Q&A templates to help the AI respond consistently to common
        questions.
      </p>
      <div class="flex gap-3">
        <Button
          color="slate"
          variant="outline"
          icon="i-lucide-download"
          label="Import CSV"
          @click="handleImportCsv"
        />
        <Button
          color="primary"
          icon="i-lucide-plus"
          label="Add Template"
          class="!bg-woot-500 hover:!bg-woot-600"
          @click="handleAdd"
        />
      </div>
    </div>

    <!-- Templates Table -->
    <div v-else class="overflow-hidden border border-n-slate-3 rounded-xl">
      <table class="w-full">
        <thead>
          <tr class="bg-n-slate-2">
            <th
              class="px-4 py-3 text-left text-xs font-semibold text-n-slate-11 uppercase tracking-wide"
            >
              Question
            </th>
            <th
              class="px-4 py-3 text-left text-xs font-semibold text-n-slate-11 uppercase tracking-wide"
            >
              Answer
            </th>
            <th
              class="px-4 py-3 text-right text-xs font-semibold text-n-slate-11 uppercase tracking-wide w-24"
            >
              Action
            </th>
          </tr>
        </thead>
        <tbody class="divide-y divide-n-slate-3">
          <tr
            v-for="(item, index) in templates"
            :key="item.id || index"
            class="hover:bg-n-slate-1 transition-colors"
            :class="
              index % 2 === 0
                ? 'bg-red-50/30 dark:bg-red-900/10'
                : 'bg-green-50/30 dark:bg-green-900/10'
            "
          >
            <td class="px-4 py-3 text-sm text-n-slate-12">
              <div class="line-clamp-2">{{ item.question }}</div>
            </td>
            <td class="px-4 py-3 text-sm text-n-slate-11">
              <div class="line-clamp-2">{{ item.answer }}</div>
            </td>
            <td class="px-4 py-3 text-right">
              <div class="flex items-center justify-end gap-1">
                <button
                  class="p-1.5 hover:bg-n-slate-3 rounded-lg transition-colors"
                  @click="handleEdit(item)"
                >
                  <span class="i-lucide-edit-2 w-4 h-4 text-n-slate-11" />
                </button>
                <button
                  class="p-1.5 hover:bg-ruby-100 dark:hover:bg-ruby-900/20 rounded-lg transition-colors"
                  @click="handleDelete(item)"
                >
                  <span class="i-lucide-trash-2 w-4 h-4 text-ruby-9" />
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Add Button (fixed bottom right) -->
    <div class="fixed bottom-6 right-6">
      <button
        class="flex items-center justify-center w-14 h-14 bg-woot-500 hover:bg-woot-600 text-white rounded-full shadow-lg hover:shadow-xl transition-all"
        @click="handleAdd"
      >
        <span class="i-lucide-plus w-6 h-6" />
      </button>
    </div>
  </div>
</template>
